import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { assessmentAPI } from "../services/assessmentAPI";
import { useAuth } from "../context/AuthContext";

const STEPS = {
  LOADING: "loading",
  START: "start",
  TAKING: "taking",
  SUBMITTING: "submitting",
  RESULTS: "results",
};

export default function RoleAssessmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Read role from URL param first — avoids the Keycloak token merge timing bug
  // where user.career_goal hasn't resolved yet when the component mounts.
  const params = new URLSearchParams(location.search);
  const role = params.get("role") || user?.career_goal || "AI/ML Engineer";

  const [step, setStep] = useState(STEPS.LOADING);
  const [assessment, setAssessment] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const startTimeRef = useRef(null);
  const questionTopRef = useRef(null);

  useEffect(() => {
    const generate = async () => {
      try {
        const data = await assessmentAPI.generate(role, 15);
        if (data.error) throw new Error(data.error);
        setAssessment(prev => prev ?? data);  // don't overwrite if already set
        setStep(prev => prev === STEPS.LOADING ? STEPS.START : prev);
      } catch (e) {
        setError(e.message || "Failed to generate assessment.");
        setStep(prev => prev === STEPS.LOADING ? STEPS.START : prev);
      }
    };
    generate();
  }, [role]);

  useEffect(() => {
    if (step === STEPS.TAKING) {
      if (questionTopRef.current) {
        questionTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [currentIndex, step]);

  const handleStart = () => {
    startTimeRef.current = Date.now();
    setStep(STEPS.TAKING);
  };

  const handleSelectAnswer = (questionId, answer) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = async () => {
    const unanswered = assessment.questions.filter(
      (q) => !answers[String(q.question_id)]
    ).length;

    if (unanswered > 0 && !showSubmitWarning) {
      setShowSubmitWarning(true);
      return;
    }

    setShowSubmitWarning(false);
    setStep(STEPS.SUBMITTING);

    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 60000);

    try {
      const data = await assessmentAPI.submit(assessment.instance_id, answers, timeSpent);
      setResult(data);
      setStep(STEPS.RESULTS);
    } catch (e) {
      setError(e.message || "Submission failed.");
      setStep(STEPS.TAKING);
    }
  };

  const currentQuestion = assessment?.questions?.[currentIndex];
  const totalQuestions = assessment?.questions?.length || 0;
  const answeredCount = Object.keys(answers).length;
  const unansweredCount = totalQuestions - answeredCount;
  const progress = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const BULLET_ITEMS = [
    "Read each question carefully before selecting an answer.",
    "You can move between questions and change your answers before submitting.",
    "You will see a warning if you try to submit with unanswered questions.",
    "Your result will show skill-by-skill feedback and a readiness score.",
  ];

  // ── LOADING ───────────────────────────────────────────────────────────────
  if (step === STEPS.LOADING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">
            Generating your assessment for{" "}
            <span className="text-purple-400 font-semibold">{role}</span>...
          </p>
          <p className="text-gray-400 text-sm mt-2">
            This may take a moment for custom roles
          </p>
        </div>
      </div>
    );
  }

  // ── ERROR ─────────────────────────────────────────────────────────────────
  if (error && step === STEPS.START) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full text-center border border-red-500/30">
          <div className="text-red-400 text-5xl mb-4">⚠</div>
          <h2 className="text-white text-xl font-semibold mb-2">Assessment Unavailable</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── START SCREEN ──────────────────────────────────────────────────────────
  if (step === STEPS.START) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-10 max-w-lg w-full border border-slate-700/50 shadow-2xl">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎯</div>
            <h1 className="text-white text-3xl font-bold mb-2">Skills Assessment</h1>
            <p className="text-purple-400 font-semibold text-lg">{role}</p>
          </div>

          <div className="bg-slate-700/50 rounded-xl p-6 mb-8 space-y-3 border border-slate-600/50">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Questions</span>
              <span className="text-white font-medium">{totalQuestions} MCQs</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Estimated time</span>
              <span className="text-white font-medium">{Math.ceil(totalQuestions * 1.5)} minutes</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Difficulty mix</span>
              <span className="text-white font-medium">Easy / Medium / Hard</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Passing score</span>
              <span className="text-white font-medium">60%</span>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-white font-semibold mb-3">Before you start</h3>
            <div className="space-y-3">
              {BULLET_ITEMS.map((text, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <span style={{ color: "#a855f7", flexShrink: 0, marginTop: "1px", fontSize: "16px", lineHeight: "1.4" }}>
                    •
                  </span>
                  <span style={{ color: "#9ca3af", fontSize: "14px", lineHeight: "1.6" }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleStart}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-4 rounded-xl transition-all text-lg"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  // ── TAKING ────────────────────────────────────────────────────────────────
  if (step === STEPS.TAKING && currentQuestion) {
    const questionId = String(currentQuestion.question_id);
    const selectedAnswer = answers[questionId];
    const options = [currentQuestion.correct_answer, ...(currentQuestion.distractors || [])].sort();

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col">
        {/* Sticky header */}
        <div
          ref={questionTopRef}
          className="bg-slate-800/60 backdrop-blur-xl border-b border-slate-700 px-6 py-4 sticky top-0 z-10"
        >
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-400 text-sm">
                Question{" "}
                <span className="text-white font-semibold">{currentIndex + 1}</span>
                {" "}of{" "}
                <span className="text-white font-semibold">{totalQuestions}</span>
              </span>
              <span
                className={`text-sm px-3 py-1 rounded-full ${
                  unansweredCount === 0
                    ? "bg-green-500/20 text-green-400"
                    : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                {unansweredCount === 0 ? "All answered" : `${unansweredCount} unanswered`}
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Question body */}
        <div className="flex-1 flex items-start justify-center p-6 pt-4">
          <div className="max-w-3xl w-full">
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl mb-4">
              <div className="flex items-start gap-3 mb-6">
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold px-3 py-1 rounded-lg shrink-0">
                  Q{currentIndex + 1}
                </span>
                <p className="text-white text-base leading-relaxed">{currentQuestion.question_text}</p>
              </div>

              <div className="space-y-3">
                {options.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectAnswer(questionId, option)}
                    className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-150 ${
                      selectedAnswer === option
                        ? "bg-purple-600/30 border-purple-500 text-white"
                        : "bg-slate-700/50 border-slate-600 text-gray-300 hover:border-purple-500/50 hover:bg-slate-700"
                    }`}
                  >
                    <span className="font-semibold text-purple-400 mr-3">
                      {String.fromCharCode(65 + idx)}.
                    </span>
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="space-y-3">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "6px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {assessment.questions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      border: "none",
                      flexShrink: 0,
                      backgroundColor:
                        idx === currentIndex
                          ? "#7c3aed"
                          : answers[String(q.question_id)]
                            ? "rgba(22, 163, 74, 0.35)"
                            : "#1e293b",
                      color:
                        idx === currentIndex
                          ? "#ffffff"
                          : answers[String(q.question_id)]
                            ? "#86efac"
                            : "#9ca3af",
                      outline: idx === currentIndex ? "2px solid #a855f7" : "none",
                      outlineOffset: "1px",
                    }}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                  marginTop: "8px",
                }}
              >
                <button
                  onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                  disabled={currentIndex === 0}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-700 text-gray-300 hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  ← Prev
                </button>

                <span style={{ color: "#6b7280", fontSize: "14px", whiteSpace: "nowrap" }}>
                  <span style={{ color: "#a855f7", fontWeight: "bold" }}>{currentIndex + 1}</span>
                  {" / "}
                  <span style={{ color: "#d1d5db" }}>{totalQuestions}</span>
                </span>

                {currentIndex < totalQuestions - 1 ? (
                  <button
                    onClick={() => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-700 text-gray-300 hover:bg-slate-600 transition-colors"
                  >
                    Next →
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold transition-all"
                  >
                    Submit
                  </button>
                )}
              </div>
            </div>

            {showSubmitWarning && (
              <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <p className="text-yellow-400 text-sm mb-3">
                  You have <strong>{unansweredCount}</strong> unanswered{" "}
                  {unansweredCount === 1 ? "question" : "questions"}. Are you sure you want to submit?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-lg transition-colors"
                  >
                    Submit anyway
                  </button>
                  <button
                    onClick={() => setShowSubmitWarning(false)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-gray-300 text-sm rounded-lg transition-colors"
                  >
                    Keep answering
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── SUBMITTING ────────────────────────────────────────────────────────────
  if (step === STEPS.SUBMITTING) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Evaluating your answers...</p>
          <p className="text-gray-400 text-sm mt-2">Calculating your readiness score</p>
        </div>
      </div>
    );
  }

  // ── RESULTS ───────────────────────────────────────────────────────────────
  if (step === STEPS.RESULTS && result) {
    const passed = result.passed;
    const readiness = result.readiness_score;
    const readinessColor =
      readiness >= 70 ? "text-green-400" : readiness >= 45 ? "text-yellow-400" : "text-red-400";

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-2xl mx-auto pt-8">
          {/* Score card */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 shadow-xl mb-6 text-center">
            <div className={`text-7xl font-bold mb-2 ${passed ? "text-green-400" : "text-red-400"}`}>
              {result.score}%
            </div>
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
                passed ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
              }`}
            >
              {passed ? "✓ Passed" : "✗ Not passed"}
            </div>
            <p className="text-gray-400 text-sm">
              {result.correct_count} correct out of {result.total} questions
            </p>
          </div>

          {/* Readiness score */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl mb-6">
            <h3 className="text-white font-semibold mb-4">Readiness Score</h3>
            <div className="flex items-center gap-4">
              <div className={`text-4xl font-bold ${readinessColor}`}>{readiness}%</div>
              <div className="flex-1">
                <div className="w-full bg-slate-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      readiness >= 70 ? "bg-green-500" : readiness >= 45 ? "bg-yellow-500" : "bg-red-500"
                    }`}
                    style={{ width: `${readiness}%` }}
                  />
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Estimated upskill time:{" "}
                  <span className="text-white">{result.estimated_upskill_hours} hours</span>
                </p>
              </div>
            </div>
          </div>

          {/* Skill breakdown */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl mb-6">
            <h3 className="text-white font-semibold mb-4">Skill Breakdown</h3>
            <div className="space-y-4">
              {result.strong_skills?.length > 0 && (
                <div>
                  <p className="text-green-400 text-xs font-semibold uppercase tracking-wider mb-2">Strong</p>
                  <div className="flex flex-wrap gap-2">
                    {result.strong_skills.map((skill) => (
                      <span key={skill} className="bg-green-500/20 text-green-300 text-sm px-3 py-1 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {result.medium_skills?.length > 0 && (
                <div>
                  <p className="text-yellow-400 text-xs font-semibold uppercase tracking-wider mb-2">Developing</p>
                  <div className="flex flex-wrap gap-2">
                    {result.medium_skills.map((skill) => (
                      <span key={skill} className="bg-yellow-500/20 text-yellow-300 text-sm px-3 py-1 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {result.weak_skills?.length > 0 && (
                <div>
                  <p className="text-red-400 text-xs font-semibold uppercase tracking-wider mb-2">Needs work</p>
                  <div className="flex flex-wrap gap-2">
                    {result.weak_skills.map((skill) => (
                      <span key={skill} className="bg-red-500/20 text-red-300 text-sm px-3 py-1 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Per-question detail */}
          <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-xl mb-6">
            <h3 className="text-white font-semibold mb-4">Question Review</h3>
            <div className="space-y-4">
              {result.detailed_results?.map((r, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-xl border ${
                    r.is_correct
                      ? "border-green-500/30 bg-green-500/5"
                      : "border-red-500/30 bg-red-500/5"
                  }`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <span className={`text-sm font-bold shrink-0 ${r.is_correct ? "text-green-400" : "text-red-400"}`}>
                      {r.is_correct ? "✓" : "✗"} Q{idx + 1}
                    </span>
                    <p className="text-gray-300 text-sm">{r.question_text}</p>
                  </div>
                  {!r.is_correct && (
                    <div className="ml-6 text-xs space-y-1">
                      <p className="text-red-400">Your answer: {r.user_answer || "Not answered"}</p>
                      <p className="text-green-400">Correct: {r.correct_answer}</p>
                      {r.explanation && <p className="text-gray-400 mt-1">{r.explanation}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={() => navigate("/dashboard")}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-4 rounded-xl transition-all text-lg mb-8"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return null;
}